import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Download,
  FileText,
  Edit,
  CheckCircle,
  Filter,
  ArrowUp,
  ArrowDown,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Card, Button, Input, Label } from "../components/ui";

export default function Relatorio() {
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState([]);

  const [showOrdenar, setShowOrdenar] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filtroNumero, setFiltroNumero] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroTecnico, setFiltroTecnico] = useState("");
  const [filtroItem, setFiltroItem] = useState("");
  const [filtroDataAbertura, setFiltroDataAbertura] = useState("");
  const [filtroDataEncerramento, setFiltroDataEncerramento] = useState("");

  const [ordenarPor, setOrdenarPor] = useState("data_abertura");
  const [ordemCrescente, setOrdemCrescente] = useState(false);

  const [modalEncerrarOpen, setModalEncerrarOpen] = useState(false);
  const [modalCancelarOpen, setModalCancelarOpen] = useState(false);

  const [osToAction, setOsToAction] = useState(null);
  const [dataEncerramentoModal, setDataEncerramentoModal] = useState("");
  const [motivoCancelamento, setMotivoCancelamento] = useState("");

  useEffect(() => {
    carregarOS();
  }, []);

  const carregarOS = async () => {
    try {
      const res = await api.get("/os");
      setOrdens(res.data);
    } catch (e) {
      toast.error("Falha ao carregar as ordens de serviço.");
    }
  };

  const baixarPDF = async (id, codigo) => {
    const loadingToast = toast.loading("Gerando PDF...");
    try {
      const payload = {
        empresa_nome: localStorage.getItem("@up2_empresa_nome") || "",
        empresa_logo: localStorage.getItem("@up2_empresa_logo") || "",
      };
      const response = await api.post(`/os/${id}/pdf`, payload, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const nomeArquivo = codigo
        ? `OS_${codigo}.pdf`
        : `OS_${id.substring(id.length - 6).toUpperCase()}.pdf`;
      link.setAttribute("download", nomeArquivo);
      document.body.appendChild(link);
      link.click();
      toast.success("PDF baixado com sucesso!", { id: loadingToast });
    } catch (e) {
      toast.error("Erro ao baixar o PDF.", { id: loadingToast });
    }
  };

  const abrirModalCancelamento = (os) => {
    setOsToAction(os);
    setMotivoCancelamento("");
    setModalCancelarOpen(true);
  };

  const confirmarCancelamento = async () => {
    if (!motivoCancelamento.trim()) {
      return toast.error("O motivo do cancelamento é obrigatório!");
    }
    try {
      const payload = {
        ...osToAction,
        status: "Cancelada",
        motivo_cancelamento: motivoCancelamento,
      };
      await api.put(`/os/${osToAction.id}`, payload);
      toast.success("OS Cancelada com sucesso!");
      setModalCancelarOpen(false);
      carregarOS();
    } catch (e) {
      toast.error("Erro ao cancelar a OS.");
    }
  };

  const abrirModalEncerramento = (os) => {
    setOsToAction(os);
    setDataEncerramentoModal(new Date().toISOString().split("T")[0]);
    setModalEncerrarOpen(true);
  };

  const confirmarEncerramento = async () => {
    if (!dataEncerramentoModal)
      return toast.error("Informe a data de encerramento!");

    try {
      const dataFormatada = dataEncerramentoModal
        .split("-")
        .reverse()
        .join("/");

      const payload = {
        ...osToAction,
        data_encerramento: dataFormatada,
        status: "Finalizada",
      };

      await api.put(`/os/${osToAction.id}`, payload);
      toast.success("OS Encerrada com sucesso!");
      setModalEncerrarOpen(false);
      carregarOS();
    } catch (e) {
      toast.error("Erro ao encerrar OS.");
    }
  };

  const limparFiltros = () => {
    setFiltroNumero("");
    setFiltroCliente("");
    setFiltroTecnico("");
    setFiltroItem("");
    setFiltroDataAbertura("");
    setFiltroDataEncerramento("");
    setOrdenarPor("data_abertura");
    setOrdemCrescente(false);
  };

  let ordensFiltradasEOrdenadas = ordens.filter((os) => {
    const codStr = os.codigo_os || os.id;
    const matchNum = codStr.toLowerCase().includes(filtroNumero.toLowerCase());
    const matchCliente = os.cliente_nome
      ?.toLowerCase()
      .includes(filtroCliente.toLowerCase());
    const matchTecnico = os.tecnico
      ?.toLowerCase()
      .includes(filtroTecnico.toLowerCase());

    const matchAbertura = filtroDataAbertura
      ? os.data_abertura === filtroDataAbertura.split("-").reverse().join("/")
      : true;
    const matchEncerramento = filtroDataEncerramento
      ? os.data_encerramento ===
        filtroDataEncerramento.split("-").reverse().join("/")
      : true;

    const matchItem = filtroItem
      ? (os.servicos &&
          os.servicos.some((s) =>
            s.descricao.toLowerCase().includes(filtroItem.toLowerCase()),
          )) ||
        (os.produtos &&
          os.produtos.some((p) =>
            p.descricao.toLowerCase().includes(filtroItem.toLowerCase()),
          ))
      : true;

    return (
      matchNum &&
      matchCliente &&
      matchTecnico &&
      matchAbertura &&
      matchEncerramento &&
      matchItem
    );
  });

  ordensFiltradasEOrdenadas.sort((a, b) => {
    let valA = a[ordenarPor] || "";
    let valB = b[ordenarPor] || "";

    if (ordenarPor === "data_abertura" || ordenarPor === "data_encerramento") {
      valA = valA ? valA.split("/").reverse().join("") : "";
      valB = valB ? valB.split("/").reverse().join("") : "";
    }

    if (valA < valB) return ordemCrescente ? -1 : 1;
    if (valA > valB) return ordemCrescente ? 1 : -1;
    return 0;
  });

  const renderizarStatus = (os) => {
    const status =
      os.status || (os.data_encerramento ? "Finalizada" : "Andamento");

    if (status === "Finalizada") {
      return (
        <div className="flex flex-col">
          <span className="text-emerald-600 font-semibold flex items-center gap-1">
            <CheckCircle size={14} /> Finalizada
          </span>
          <span className="text-xs text-slate-400 font-medium">
            {os.data_encerramento}
          </span>
        </div>
      );
    }
    if (status === "Cancelada") {
      return (
        <div className="flex flex-col">
          <span className="text-red-500 font-semibold flex items-center gap-1">
            <XCircle size={14} /> Cancelada
          </span>
        </div>
      );
    }

    return <span className="text-amber-500 font-semibold">Em Andamento</span>;
  };

  return (
    <div className="space-y-6 animate-in fade-in pb-20 relative">
      <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-end gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            Relatório Completo
          </h2>
          <p className="text-slate-500">
            Relação completa de todas as OS geradas no sistema.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className={`w-full sm:w-auto ${showFilters ? "bg-slate-100" : ""}`}
        >
          <Filter size={16} className="mr-2" /> Filtros e Ordenação
        </Button>
      </div>

      {showFilters && (
        <Card className="p-5 bg-slate-50 animate-in fade-in slide-in-from-top-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-xs text-slate-500">Nº da OS</Label>
              <Input
                placeholder="Ex: 2026..."
                value={filtroNumero}
                onChange={(e) => setFiltroNumero(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Cliente</Label>
              <Input
                placeholder="Nome do cliente"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Técnico</Label>
              <Input
                placeholder="Nome do técnico"
                value={filtroTecnico}
                onChange={(e) => setFiltroTecnico(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">
                Serviço ou Produto
              </Label>
              <Input
                placeholder="Peça ou reparo"
                value={filtroItem}
                onChange={(e) => setFiltroItem(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Data de Abertura</Label>
              <Input
                type="date"
                value={filtroDataAbertura}
                onChange={(e) => setFiltroDataAbertura(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">
                Data de Encerramento
              </Label>
              <Input
                type="date"
                value={filtroDataEncerramento}
                onChange={(e) => setFiltroDataEncerramento(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <div className="md:col-span-2 w-full">
                <Label className="text-xs text-slate-500">Ordenar por</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      readOnly
                      value={
                        ordenarPor === "codigo_os"
                          ? "Número da OS"
                          : ordenarPor === "data_abertura"
                            ? "Data de Abertura"
                            : "Data de Encerramento"
                      }
                      onClick={() => setShowOrdenar(!showOrdenar)}
                      onBlur={() =>
                        setTimeout(() => setShowOrdenar(false), 200)
                      }
                      className="cursor-pointer bg-white"
                    />
                    {showOrdenar && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden">
                        <div
                          className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700"
                          onClick={() => {
                            setOrdenarPor("codigo_os");
                            setShowOrdenar(false);
                          }}
                        >
                          Número da OS
                        </div>
                        <div
                          className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700 border-t border-slate-100"
                          onClick={() => {
                            setOrdenarPor("data_abertura");
                            setShowOrdenar(false);
                          }}
                        >
                          Data de Abertura
                        </div>
                        <div
                          className="px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm text-slate-700 border-t border-slate-100"
                          onClick={() => {
                            setOrdenarPor("data_encerramento");
                            setShowOrdenar(false);
                          }}
                        >
                          Data de Encerramento
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    className="px-3 shrink-0"
                    onClick={() => setOrdemCrescente(!ordemCrescente)}
                  >
                    {ordemCrescente ? (
                      <ArrowUp size={16} />
                    ) : (
                      <ArrowDown size={16} />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end border-t border-slate-200 pt-3 mt-2">
            <button
              onClick={limparFiltros}
              className="text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-sm text-left min-w-[900px]">
            <thead className="bg-slate-50 border-b text-slate-700">
              <tr>
                <th className="px-6 py-4 font-medium">Nº da OS</th>
                <th className="px-6 py-4 font-medium">Responsável</th>
                <th className="px-6 py-4 font-medium">Cliente</th>
                <th className="px-6 py-4 font-medium">Técnico</th>
                <th className="px-6 py-4 font-medium">Abertura</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Total</th>
                <th className="px-6 py-4 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {ordensFiltradasEOrdenadas.map((os) => (
                <tr
                  key={os.id}
                  className={`transition-colors ${os.status === "Cancelada" ? "bg-red-50/40 opacity-75" : "hover:bg-slate-50"}`}
                >
                  <td className="px-6 py-4 font-mono font-semibold text-slate-700">
                    {os.codigo_os ||
                      os.id.substring(os.id.length - 6).toUpperCase()}
                  </td>
                  <td className="px-6 py-4">{os.criado_por || "Sistema"}</td>
                  <td className="px-6 py-4 font-medium text-slate-900">
                    {os.cliente_nome}
                  </td>
                  <td className="px-6 py-4">{os.tecnico}</td>
                  <td className="px-6 py-4">{os.data_abertura}</td>
                  <td className="px-6 py-4">{renderizarStatus(os)}</td>
                  <td className="px-6 py-4 font-medium">
                    R$ {(os.valor_total || 0).toFixed(2).replace(".", ",")}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {os.status !== "Finalizada" &&
                        os.status !== "Cancelada" && (
                          <Button
                            variant="success"
                            onClick={() => abrirModalEncerramento(os)}
                            className="h-8 px-3"
                            title="Encerrar OS"
                          >
                            <CheckCircle size={14} className="mr-1" /> Encerrar
                          </Button>
                        )}

                      {os.status !== "Cancelada" && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/editar/${os.id}`)}
                          className="h-8 px-2"
                          title="Editar"
                        >
                          <Edit size={16} className="text-slate-600" />
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        onClick={() => baixarPDF(os.id, os.codigo_os)}
                        className="h-8 px-2"
                        title="Baixar PDF"
                      >
                        <Download size={16} className="text-slate-600" />
                      </Button>

                      {os.status !== "Cancelada" && (
                        <Button
                          variant="outline"
                          onClick={() => abrirModalCancelamento(os)}
                          className="h-8 px-2 border-red-200 text-red-500 hover:bg-red-50"
                          title="Cancelar OS"
                        >
                          <XCircle size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {modalEncerrarOpen && createPortal(
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 animate-in fade-in px-4">
          <Card className="p-6 w-[95%] sm:w-full max-w-md shadow-2xl mx-auto">
            <h3 className="text-xl font-bold mb-2 text-slate-900">Encerrar OS</h3>
            <p className="text-sm text-slate-500 mb-4">
              Defina a data de conclusão para a OS de <span className="font-bold text-slate-700">{osToAction?.cliente_nome}</span>.
            </p>
            <div className="mb-6">
              <Label>Data de Encerramento</Label>
              <Input type="date" value={dataEncerramentoModal} onChange={(e) => setDataEncerramentoModal(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModalEncerrarOpen(false)}>Voltar</Button>
              <Button variant="success" onClick={confirmarEncerramento}>Confirmar</Button>
            </div>
          </Card>
        </div>,
        document.body
      )}

      {modalCancelarOpen && createPortal(
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 animate-in fade-in px-4">
          <Card className="p-6 w-[95%] sm:w-full max-w-md shadow-2xl border-t-4 border-t-red-500 mx-auto">
            <h3 className="text-xl font-bold mb-2 text-red-600">Cancelar OS</h3>
            <p className="text-sm text-slate-600 mb-4">Esta ação marcará a OS como Cancelada permanentemente.</p>
            <div className="mb-6">
              <Label>Motivo do Cancelamento</Label>
              <textarea
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-400 min-h-[100px] resize-none"
                placeholder="Ex: Cliente desistiu do serviço..."
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
              ></textarea>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModalCancelarOpen(false)}>Sair</Button>
              <Button variant="destructive" onClick={confirmarCancelamento}>Confirmar</Button>
            </div>
          </Card>
        </div>,
        document.body
      )}
    </div>
  );
}